import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useNavigate, useParams } from '@tanstack/react-router';
import { api } from '~portal/api';
import { logger } from '~portal/lib';

/**
 * @desc Shared delete-conversation flow: Charlie's confirmation prompt (keep = accent,
 * delete = destructive), the `conversations.remove` mutation, list invalidation and — when the
 * deleted conversation is the one on screen — navigation back to a fresh chat.
 *
 * Shared so the sidebar row's delete affordance and the chat sub-header's delete button cannot
 * drift apart.
 */
export function useDeleteConversation() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const currentConversationId = 'conversationId' in params ? params.conversationId : undefined;
  const utils = api.useUtils();

  const deleteMutation = api.conversations.remove.useMutation({
    onSuccess: () => {
      utils.conversations.list.invalidate().catch((err) => {
        logger.error('[useDeleteConversation] Failed to invalidate conversations list', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    },
  });

  const confirmDelete = (conversationId: string) => {
    if (!conversationId || conversationId === 'new') return;

    modals.openConfirmModal({
      title: 'Delete conversation',
      centered: true,
      radius: 'lg',
      children: (
        <Text size="sm">
          Would you like to delete this conversation? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Keep' },
      confirmProps: { color: 'red', radius: 'xl' },
      cancelProps: { variant: 'filled', color: 'blue', radius: 'xl' },
      groupProps: { justify: 'center' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync({ conversationId });
        } catch (err) {
          // Backend scopes deletion by owner, so a foreign conversation no-ops with NOT_FOUND.
          // Log and bail rather than navigating away.
          logger.error('[useDeleteConversation] Delete conversation failed', {
            conversationId,
            error: err instanceof Error ? err.message : String(err),
          });
          return;
        }

        if (currentConversationId !== conversationId) return;

        navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } }).catch(
          (err) => {
            logger.error('[useDeleteConversation] Navigation after delete failed', {
              conversationId,
              error: err instanceof Error ? err.message : String(err),
            });
          },
        );
      },
    });
  };

  return { confirmDelete, isDeleting: deleteMutation.isPending };
}
